<template>
  <Layout>
    <PageHeader :title="$t('ticketManager.title')" :items="breadcrumbItems" />

    <div v-if="pageLoading" class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body text-center py-5">
            <div class="spinner-border text-primary mb-2" role="status" style="width: 3rem; height: 3rem;">
              <span class="visually-hidden">Loading</span>
            </div>
            <p class="text-muted mb-0">{{ $t('ticketManager.loading') }}</p>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <p class="text-muted mb-4">{{ $t('ticketManager.subtitle') }}</p>

            <div class="row mb-4">
              <div class="col-md-4">
                <label class="form-label">{{ $t('ticketManager.guiche') }}</label>
                <input
                  v-model.number="guiche"
                  type="number"
                  class="form-control"
                  min="1"
                  placeholder="1"
                />
              </div>
              <div class="col-md-4 d-flex align-items-end">
                <button
                  type="button"
                  class="btn btn-primary"
                  :disabled="callingNext || !guiche"
                  @click="callNext"
                >
                  <span v-if="callingNext" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  {{ $t('ticketManager.callNext') }}
                </button>
              </div>
            </div>

            <div v-if="currentCalled" class="alert alert-info d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div>
                <strong>{{ $t('ticketManager.currentCalled') }}:</strong>
                <span class="ms-2 fs-5">{{ currentCalled.codigo }}</span>
                <span class="badge bg-secondary ms-2">{{ currentCalled.tipo }}</span>
                <span class="ms-2">{{ $t('ticketManager.guiche') }} {{ currentCalled.guiche }}</span>
              </div>
              <button
                type="button"
                class="btn btn-success"
                :disabled="markingAtendido"
                @click="markAsAtendido(currentCalled.id)"
              >
                <span v-if="markingAtendido" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                {{ $t('ticketManager.markAsAtendido') }}
              </button>
            </div>

            <div v-if="lastMessage" class="alert mb-0" :class="lastMessageType === 'error' ? 'alert-danger' : lastMessageType === 'info' ? 'alert-info' : 'alert-success'" role="alert">
              {{ lastMessage }}
            </div>
          </div>
        </div>

        <div class="card mt-3">
          <div class="card-body">
            <h6 class="card-title mb-3">{{ $t('ticketManager.generateSenha') }}</h6>
            <div class="row align-items-end">
              <div class="col-md-4">
                <label class="form-label">{{ $t('ticketManager.tipoNormal') }} / {{ $t('ticketManager.tipoPreferencial') }}</label>
                <select v-model="generateTipo" class="form-select">
                  <option value="normal">{{ $t('ticketManager.tipoNormal') }}</option>
                  <option value="preferencial">{{ $t('ticketManager.tipoPreferencial') }}</option>
                </select>
              </div>
              <div class="col-md-4">
                <button
                  type="button"
                  class="btn btn-outline-primary"
                  :disabled="generating"
                  @click="generateSenha"
                >
                  <span v-if="generating" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  {{ $t('ticketManager.generateSenha') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/views/layouts/main';
import PageHeader from '@/components/page-header';
import TicketsController from '@/controller/TicketsController';

export default {
  name: 'TicketManager',
  components: { Layout, PageHeader },
  data() {
    return {
      breadcrumbItems: [
        { text: this.$t('menuitems.processes.text'), href: '#' },
        { text: this.$t('menuitems.processes.list.tickets-manager'), active: true }
      ],
      guiche: 1,
      currentCalled: null,
      callingNext: false,
      markingAtendido: false,
      generating: false,
      generateTipo: 'normal',
      lastMessage: '',
      lastMessageType: 'success',
      pageLoading: true
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.pageLoading = false;
    });
  },
  created() {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const empresa = JSON.parse(localStorage.getItem('currentEmpresa') || '{}');
    this.ticketsController = new TicketsController(user, empresa);
  },
  methods: {
    setMessage(msg, type = 'success') {
      this.lastMessage = msg;
      this.lastMessageType = type;
    },
    async callNext() {
      if (!this.guiche || this.callingNext) return;
      this.callingNext = true;
      this.lastMessage = '';
      try {
        const res = await this.ticketsController.callNext(this.guiche);
        if (res.data) {
          this.currentCalled = res.data;
          this.setMessage(this.$t('ticketManager.callSuccess'));
        } else {
          this.currentCalled = null;
          this.setMessage(this.$t('ticketManager.noPending'), 'info');
        }
      } catch (e) {
        this.setMessage(this.$t('ticketManager.error'), 'error');
      } finally {
        this.callingNext = false;
      }
    },
    async markAsAtendido(id) {
      if (!id || this.markingAtendido) return;
      this.markingAtendido = true;
      this.lastMessage = '';
      try {
        const res = await this.ticketsController.markAsAtendido(id);
        if (res.data) {
          this.currentCalled = null;
          this.setMessage(this.$t('ticketManager.markSuccess'));
        }
      } catch (e) {
        this.setMessage(this.$t('ticketManager.error'), 'error');
      } finally {
        this.markingAtendido = false;
      }
    },
    async generateSenha() {
      if (this.generating) return;
      this.generating = true;
      this.lastMessage = '';
      try {
        await this.ticketsController.generate(this.generateTipo);
        this.setMessage(this.$t('ticketManager.generateSuccess'));
      } catch (e) {
        this.setMessage(this.$t('ticketManager.error'), 'error');
      } finally {
        this.generating = false;
      }
    }
  }
};
</script>
